// src/api/aiEdgeFunctions.js
//
// Wrapper para as 4 Edge Functions de IA do projeto.
// Endpoints (já em produção · não tocar nas funções server-side aqui):
//   - ai-insight      · insight financeiro do Dashboard (Claude Haiku)
//   - ai-insight-rh   · insight RH com LGPD-safe (Claude Haiku)
//   - ai-conciliacao  · conciliação bancária extrato → fluxo_caixa (Claude Haiku)
//   - ai-search       · busca global com IA (Claude Haiku)
//
// 🛡 Defesa em profundidade (decisão Rafael 28/05/2026):
// Cada chamada inclui `_tenant_check` no body com o empresaId do tenantContext.
// O Edge Function compara contra o JWT.empresa_id; se divergir → 403.
// Isto fecha o vetor: token roubado de outro tenant tentando chamar com contexto
// do tenant atual da vítima.

import { sb } from './supabaseClient.js';
import { tenant } from '../context/tenantContext.js';

const URL = import.meta.env.VITE_SUPABASE_URL;
const KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Núcleo genérico de chamada a Edge Function de IA.
 * @param {string} fnName — nome da Edge Function (sem prefixo)
 * @param {object} body   — payload específico da função
 * @returns {Promise<object>} resposta JSON do Edge
 */
async function _callAiEdge(fnName, body = {}) {
  if (!tenant.isHydrated) {
    throw new Error(`[aiEdge] callAiEdge("${fnName}") sem tenant hidratado.`);
  }

  // Sessão atual (token de auth)
  const { data: { session }, error: sessErr } = await sb.auth.getSession();
  if (sessErr || !session) {
    throw new Error(`[aiEdge] sessão Supabase indisponível: ${sessErr?.message || 'no session'}`);
  }

  const url = `${URL}/functions/v1/${fnName}`;

  // 🛡 Defesa: cliente declara qual tenant ESTÁ tentando consultar.
  // Server compara contra JWT.empresa_id. Discrepância → 403.
  const payload = {
    ...body,
    _tenant_check: tenant.empresaId, // null pra super_admin
  };

  const _ctrl = new AbortController();
  const _to = setTimeout(() => _ctrl.abort(), 18000);
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: _ctrl.signal,
    });
  } finally {
    clearTimeout(_to);
  }

  if (!res.ok) {
    let errBody = null;
    try { errBody = await res.json(); } catch {}
    const message = errBody?.message || errBody?.error || res.statusText;
    const code = errBody?.code || `HTTP_${res.status}`;
    const error = new Error(`[aiEdge ${fnName}] ${code}: ${message}`);
    error.code = code;
    error.status = res.status;
    error.body = errBody;
    throw error;
  }

  return res.json();
}

// ── API pública · um endpoint por função ────────────────────────────

/**
 * Insight financeiro do Dashboard (BI-001).
 * Plano BASIC retorna heurística; Plano PRO chama Claude.
 *
 * @param {object} context — KPIs já calculados no front (obras_atrasadas, caixa_descoberto, etc)
 */
export function aiInsight(context) {
  return _callAiEdge('ai-insight', { context });
}

/**
 * Insight RH (RH-001 Dashboard).
 * Sanitiza PII server-side antes de mandar pra Claude (LGPD-safe).
 *
 * @param {object} context — KPIs RH + funcionários por matrícula (nunca nome)
 */
export function aiInsightRH(context) {
  return _callAiEdge('ai-insight-rh', { context });
}

/**
 * Conciliação bancária (FIN-CAIXA Conciliação IA).
 *
 * @param {object} params
 * @param {string} params.texto_extrato — texto sanitizado do PDF/CSV/XLSX
 * @param {Array<object>} params.fluxo_caixa_pendente — lançamentos ainda não conciliados
 */
export function aiConciliacao(params) {
  return _callAiEdge('ai-conciliacao', params);
}

/**
 * Busca global com IA (V2-Topbar.3).
 * Apenas Plano PRO; BASIC vê upsell no UI antes de chegar aqui.
 *
 * @param {string} query — pergunta do user em linguagem natural
 * @param {object} context — top obras/clientes + KPIs LGPD-safe
 */
export function aiSearch(query, context) {
  return _callAiEdge('ai-search', { query, context });
}
